import type { WorkspaceOperationInOut } from '@festina/configuration-general-types/configuration'
import {
  configurationKeys,
  type LayerKey,
  useAddOrUpdateWorkspaceOperation,
} from '@festina-life/api'
import type { ConfigurableServer } from '@festina-life/core'
import { useDebounceCallback } from '@festina-life/ui/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import type { ChangeOperation } from '../../workspace/types'

type AddWorkspaceOperationsInputArguments = {
  server: ConfigurableServer
  name: string
  branchName: string
  layer: LayerKey
  block: string
  operation: ChangeOperation
}

export const useAddOrUpdateWorkspaceOperationWithInvalidation = () => {
  const queryClient = useQueryClient()
  const { mutateAsync } = useAddOrUpdateWorkspaceOperation()
  const debouncedInvalidateErrors = useDebounceCallback(
    async ({
      name,
      server,
      branchName,
    }: {
      name: string
      branchName: string
      server: ConfigurableServer
      block: string
    }) => {
      await queryClient.invalidateQueries({
        queryKey: configurationKeys.fetchBranchAllErrorsPrefix({
          server,
          branchName,
        }),
      })

      await queryClient.invalidateQueries({
        queryKey: configurationKeys.fetchWorkspaceAllErrorsKeyPrefix({
          workspaceName: name,
          server,
        }),
      })
    },
    500,
    [queryClient],
  )

  return useCallback(
    async ({
      name,
      server,
      layer,
      block,
      branchName,
      operation,
    }: AddWorkspaceOperationsInputArguments) => {
      const triggeredOperation = operation?.operationKey
        ? operation
        : {
            ...operation,
            operationKey: crypto.randomUUID(),
          }

      const triggeredOperationInOut = {
        _type: 'WorkspaceOperationInOut',
        operation: triggeredOperation,
        layer: layer.layerId,
        block,
        tenantId: layer.tenantId,
      } satisfies WorkspaceOperationInOut

      const optimisticallyUpdateOperations = () => {
        const currentOperations = queryClient.getQueryData<
          WorkspaceOperationInOut[]
        >(
          configurationKeys.fetchWorkspaceOperationsKey({
            workspaceName: name,
            server,
          }),
        )

        if (!currentOperations) {
          return currentOperations
        }

        const currentOperationIndex = currentOperations.findIndex(
          (op) =>
            op.operation?.operationKey === triggeredOperation?.operationKey,
        )

        const nextOperations = [...currentOperations]

        if (currentOperationIndex === -1) {
          nextOperations.push(triggeredOperationInOut)
        } else {
          nextOperations[currentOperationIndex] = triggeredOperationInOut
        }

        queryClient.setQueryData<ChangeOperation[]>(
          configurationKeys.fetchWorkspaceOperationsKey({
            workspaceName: name,
            server,
          }),
          nextOperations,
        )
      }

      optimisticallyUpdateOperations()

      await mutateAsync({
        name,
        server,
        operation: triggeredOperationInOut,
      })

      await debouncedInvalidateErrors({
        name,
        server,
        branchName,
        block,
      })
    },
    [mutateAsync, queryClient, debouncedInvalidateErrors],
  )
}
